import React, { useState, useCallback } from 'react';
import { Box, Button, ButtonGroup, Paper, Skeleton, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { CompareApprovalsProps, GqlCreditSubmissionApproval } from '../types';
import { useCompareApprovals } from '../hooks';
import { ApprovalTermsTable, ConditionsSection, ReviewExtractedTerms } from '../components';
import { DecisionLetterUploadModal } from './DecisionLetterUploadModal';

export const CompareApprovals: React.FC<CompareApprovalsProps> = ({ vo, voId, dynamicsVoId, prescreenContent }) => {
  const { activeTab, setActiveTab, approvals, rawCreditSubmissions, badgesByRow, loading, refetch } =
    useCompareApprovals(dynamicsVoId);

  const [uploadOpen, setUploadOpen] = useState(false);

  const [refetching, setRefetching] = useState(false);

  // When a lender column is clicked, show the review view for that parsed decision
  const [reviewTarget, setReviewTarget] = useState<{
    cs: GqlCreditSubmissionApproval;
    parsedDecisionId: string;
  } | null>(null);

  const handleEditApproval = useCallback(
    (parsedDecisionId: string) => {
      const cs = rawCreditSubmissions.find(c => c.creditDecisionParsedData?.parsedDecisionId === parsedDecisionId);
      if (cs) {
        setReviewTarget({ cs, parsedDecisionId });
      }
    },
    [rawCreditSubmissions]
  );

  const handleReviewSaveComplete = useCallback(() => {
    setRefetching(true);
    refetch().finally(() => setRefetching(false));
  }, [refetch]);

  const handleDecisionLetterFlowCompleted = useCallback(() => {
    setUploadOpen(false);
    setRefetching(true);
    refetch().finally(() => setRefetching(false));
  }, [refetch]);

  if (!loading && approvals.length === 0 && prescreenContent === undefined) {
    return null;
  }

  // Review view (inline, replaces the table)
  if (reviewTarget) {
    return (
      <Paper sx={{ p: 3 }}>
        <ReviewExtractedTerms
          creditSubmission={reviewTarget.cs}
          parsedDecisionId={reviewTarget.parsedDecisionId}
          onBack={() => setReviewTarget(null)}
          onSaveComplete={handleReviewSaveComplete}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header: Tabs + Upload button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        <ButtonGroup
          variant="outlined"
          size="small"
          sx={{
            borderRadius: '8px',
            overflow: 'hidden',
            '& .MuiButtonGroup-grouped': {
              minWidth: 150,
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.8rem',
              py: 1,
              px: 2.5,

              borderColor: 'primary.main',
            },
          }}
        >
          {prescreenContent !== undefined && (
            <Button
              variant={activeTab === 'prescreen' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('prescreen')}
              sx={{ ml: 1 }}
            >
              Prescreen Results
            </Button>
          )}
          {approvals.length > 0 && (
            <Button
              variant={activeTab === 'approvals' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('approvals')}
              sx={{ mr: 1 }}
            >
              Approvals
            </Button>
          )}
        </ButtonGroup>

        <Button
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          size="small"
          disabled={!vo}
          onClick={() => setUploadOpen(true)}
        >
          Upload Decision Letter
        </Button>
      </Box>

      {vo && (
        <DecisionLetterUploadModal
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onCompleted={handleDecisionLetterFlowCompleted}
          vo={vo}
          dynamicsVoId={dynamicsVoId}
        />
      )}

      {/* Tab Content */}
      {activeTab === 'approvals' && (
        <Box>
          <Typography variant="caption" fontSize="20px" fontWeight={700} sx={{ mb: 2 }}>
            Compare Approvals
          </Typography>

          {loading || refetching ? (
            <Box>
              <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 1 }} />
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1, mb: 0.5 }} />
              ))}
            </Box>
          ) : (
            <>
              <ApprovalTermsTable approvals={approvals} badgesByRow={badgesByRow} onEdit={handleEditApproval} />
              <ConditionsSection approvals={approvals} conditionsBadges={badgesByRow['conditions'] ?? {}} />
            </>
          )}
        </Box>
      )}

      {activeTab === 'prescreen' && (
        <Box>
          {prescreenContent ?? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Prescreen results will be available here.
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};
